import {
  Box,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { RouteComponentProps, useLocation } from "@reach/router";
import PersonIcon from "@material-ui/icons/Person";
import { format } from "date-fns";
import {
  UrlStatus,
  ContentFilterObject,
  ContentSearchResult,
} from "@authory-packages/core";
import { useState } from "react";
import { useFormik } from "formik";
import { Layout } from "./Layout";
import { Api } from "../lib/api";
import { parse } from "query-string";
import { useNavigate } from "@reach/router";
import { ComponentLoadingIndicator } from "../components/ComponentLoadingIndicator";
import { Routes } from "../routes";
import { useEffect } from "react";
import {
  IconStyles,
  PaginationStyles,
  StyledTableCell,
  TableColumnstyles,
} from "../styles";
import { useSnackbar } from "notistack";
import { enumToOptions } from "../lib/enumToOption";
import { getUrlStatusLabel } from "../lib/labelNames";

const ROWS_PER_PAGE = 20;

export interface UrlPageProps extends RouteComponentProps<{}> {}

export const URLPage = ({ path }: UrlPageProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const paginationClasses = PaginationStyles();
  const tableColumnClasses = TableColumnstyles();
  const iconClasses = IconStyles();

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = parse(location.search);

  const [searchResults, setSearchResults] = useState<ContentSearchResult>();
  const [urlLoading, setUrlLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      mainSearch: (searchParams.mainSearch as string) || "",
      urlSearch: (searchParams.urlSearch as string) || "",
      page: parseInt(searchParams.page as string) || 0,
      status: (searchParams.status as string) || "All",
    },
    onSubmit: async (data) => {
      const queryParams: string[] = [];

      for (const [key, value] of Object.entries(data)) {
        //@ts-ignore
        if (data[key]) queryParams.push(`${key}=${value}`);
      }

      await navigate(`${Routes.URl}?${queryParams.join("&")}`);

      setUrlLoading(true);

      const payloadData: ContentFilterObject = {
        skip: data.page * ROWS_PER_PAGE,
        take: ROWS_PER_PAGE,
      };

      if (data.status !== "All") {
        payloadData.status = data.status as UrlStatus;
      }

      if (data.mainSearch.startsWith("User")) {
        payloadData.userid = parseInt(data.mainSearch.replace("User", ""));
      } else if (data.mainSearch.startsWith("Root")) {
        payloadData.rootid = parseInt(data.mainSearch.replace("Root", ""));
      } else if (data.mainSearch.startsWith("Website")) {
        payloadData.sourceid = parseInt(data.mainSearch.replace("Website", ""));
      } else {
        payloadData.user = data.mainSearch;
      }

      if (data.urlSearch.startsWith("Article")) {
        payloadData.articleid = parseInt(data.urlSearch.replace("Article", ""));
      } else if (data.urlSearch.startsWith("Url")) {
        payloadData.urlid = parseInt(data.urlSearch.replace("Url", ""));
      } else {
        payloadData.url = data.urlSearch;
      }

      try {
        const res = await Api.getContentGeneric(payloadData);

        setSearchResults(res.data);

        setUrlLoading(false);
      } catch {
        setUrlLoading(false);
      }
    },
    enableReinitialize: true,
  });

  // Fire search if form is populated initially
  useEffect(() => {
    if (!!formik.values.mainSearch.length || !!formik.values.urlSearch.length)
      formik.submitForm();
  }, [location.href]);

  // Generate status select options from enum
  const statusOptions = enumToOptions(UrlStatus, getUrlStatusLabel);

  return (
    <Layout title="Urls">
      <Box mb={2} component={Paper} padding={2}>
        <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <TextField
                fullWidth
                label="User/Slug/Source"
                name="mainSearch"
                value={formik.values.mainSearch}
                onChange={(e) => {
                  formik.setFieldValue("page", 0);
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Url search"
                name="urlSearch"
                value={formik.values.urlSearch}
                onChange={(e) => {
                  formik.setFieldValue("page", 0);
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={2}>
              <InputLabel id="label">Status</InputLabel>
              <Select
                fullWidth
                name="status"
                value={formik.values.status}
                onChange={(e) => {
                  formik.setFieldValue("page", 0);
                  formik.handleChange(e);
                  formik.submitForm();
                }}
                onBlur={formik.handleBlur}
              >
                <MenuItem key={""} value="All">
                  {"All"}
                </MenuItem>
                {statusOptions.map((st) => (
                  <MenuItem key={st.value} value={st.value}>
                    {st.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <input type="submit" hidden />
        </form>
      </Box>
      <ComponentLoadingIndicator isLoading={urlLoading}>
        {searchResults && (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Id</StyledTableCell>
                    <StyledTableCell>User</StyledTableCell>
                    <StyledTableCell className={tableColumnClasses.url}>
                      Link
                    </StyledTableCell>
                    <StyledTableCell>Analyze Author</StyledTableCell>
                    <StyledTableCell>Analyze Date</StyledTableCell>
                    <StyledTableCell>Text Length</StyledTableCell>
                    <StyledTableCell>Meta Image</StyledTableCell>
                    <StyledTableCell>HTML Images</StyledTableCell>
                    <StyledTableCell>Found / Created</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Article</StyledTableCell>
                    <StyledTableCell>Image Download Status</StyledTableCell>
                    <StyledTableCell className={tableColumnClasses.actions}>
                      Actions
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.urls.map((res) => {
                    let url = "";
                    let urlText = "";
                    let ssri = "";
                    let ssriText = "";
                    let differentUrl = false;

                    if (
                      res.articleUrl !== res.ssri &&
                      res.articleUrl !== null &&
                      res.ssri !== null
                    ) {
                      differentUrl = true;
                      url = res.articleUrl;
                      urlText = `Res: ${res.articleUrl}`;
                      ssri = res.ssri;
                      ssriText = `Fnd: ${res.ssri}`;
                    } else {
                      url = res.ssri || res.articleUrl;
                      urlText = url;
                    }

                    return (
                      <TableRow key={res.id}>
                        <StyledTableCell component="td" scope="row">
                          {res.id ? `Url${res.id}` : `${res.articleTraceId}`}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          <Link href={`/user/${res.userSlug}`}>
                            {res.userSlug}
                          </Link>
                        </StyledTableCell>
                        <StyledTableCell
                          className={tableColumnClasses.url}
                          component="td"
                          scope="row"
                        >
                          {differentUrl ? (
                            <>
                              <Link href={url} target="_blank">
                                {urlText}
                              </Link>
                              <br />
                              <Link href={ssri} target="_blank">
                                {ssriText}
                              </Link>
                            </>
                          ) : (
                            <Link href={url} target="_blank">
                              {urlText}
                            </Link>
                          )}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {res.foundAuthor
                            ? res.foundAuthor.split("\n").map((line) => (
                                <>
                                  <span>{line}</span>
                                  <br />
                                </>
                              ))
                            : "-"}
                        </StyledTableCell>
                        <StyledTableCell
                          className={tableColumnClasses.date}
                          component="td"
                          scope="row"
                        >
                          {res.foundDate
                            ? format(new Date(res.foundDate), "dd MMM yyyy")
                            : "-"}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {res.textLength ? res.textLength : "-"}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {res.staticImagesAvailable ? "Yes" : "No"}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {res.htmlImagesAvailable ? "Yes" : "No"}
                        </StyledTableCell>
                        <StyledTableCell
                          className={tableColumnClasses.date}
                          component="td"
                          scope="row"
                        >
                          {res.foundDate
                            ? format(new Date(res.foundDate), "dd MMM yyyy")
                            : res.articleCreatedTimestamp
                              ? format(
                                  new Date(res.articleCreatedTimestamp),
                                  "dd MMM yyyy",
                                )
                              : "-"}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {res.status
                            ? `${res.status} ${
                                res.rejectReason ? res.rejectReason : ""
                              }`
                            : res.articleManuallyAdded
                              ? "Manually Added"
                              : ""}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {res.articleTraceId ? (
                            <Link
                              href={`https://authory.com/${res.userSlug}/${res.articleSlug}`}
                              target="_blank"
                            >
                              {res.articleTraceId}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {res.imageDownloadStatus
                            ? res.imageDownloadStatus
                            : "-"}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {res.articleCanonicalSlug && (
                            <Tooltip title="Open article in god mode">
                              <PersonIcon
                                className={iconClasses.root}
                                color="action"
                                onClick={async () => {
                                  const impersonateRes = await Api.impersonate(
                                    res.userSlug,
                                  );
                                  await window.open(
                                    `${impersonateRes.data.redirectTo}&redirect=/app/content/${res.articleCanonicalSlug}`,
                                    "_blank",
                                  );
                                }}
                              />
                            </Tooltip>
                          )}
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={2} className={paginationClasses.root}>
              <TablePagination
                component="div"
                count={searchResults.count}
                page={formik.values.page}
                onChangePage={(_, page) => {
                  formik.setFieldValue("page", page);
                  formik.submitForm();
                }}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[]}
              />
            </Box>
          </>
        )}
      </ComponentLoadingIndicator>
    </Layout>
  );
};
